import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import gfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import ExternalRouteButton from "components/ExternalRouteButton";
import css from "./MarkdownRenderer.module.scss";

const MARKDOWN_TABLE_WITH_EXACTLY_TWO_COLUMNS = new RegExp(
  /^(\|([^|]+)\|([^|]*)\|)+$/
);

/**
 * Heuristic to detect known tables with item titles in the first column and values in the second.
 * @param {string} value Markdown value to check
 * @return {boolean} whether the value contains a table with two exactly columns (and nothing more)
 */
export function containsOnlyTableWithTwoColumns(value) {
  return MARKDOWN_TABLE_WITH_EXACTLY_TWO_COLUMNS.test(
    // remove all whitespace characters from the value so the regular expression does not have to deal with them
    value.replaceAll(/\s+/g, "")
  );
}

const MarkdownRenderer = ({value = "", tableFormat, className}) => {
  const mdClassName = classNames(className, css.markdownRenderer, {
    [css.tableAsText]: tableFormat === "text",
    [css.tableAsItems]:
      tableFormat === "itemsIfTwoColumns" &&
      containsOnlyTableWithTwoColumns(value),
    [css.tableHorizontallyScrollable]:
      (tableFormat === "itemsIfTwoColumns" &&
        !containsOnlyTableWithTwoColumns(value)) ||
      typeof tableFormat === "undefined"
  });

  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw, rehypeSanitize]}
      remarkPlugins={[gfm, remarkBreaks]}
      className={mdClassName}
      components={{
        a: ({href, children}) => (
          <ExternalRouteButton
            asLink
            text={children}
            href={`${window.publicURL}${href}`}
          />
        ),
        p: ({children, ...props}) => <div {...props}>{children}</div>
      }}>
      {value}
    </ReactMarkdown>
  );
};

MarkdownRenderer.propTypes = {
  value: PropTypes.string,
  tableFormat: PropTypes.oneOf(["text", "itemsIfTwoColumns"])
};

export default MarkdownRenderer;
